import { useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { RoundIconButton } from '@shared/components/button';
import { grid } from '@shared/styles';
import { useMountedRef } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface UrlActionButtonsProps {
  url: string;
  showCopy?: boolean;
  showOpen?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const COPY_TOOLTIP_DURATION_MS = 3000;

export function UrlActionButtons({
  url,
  showCopy = true,
  showOpen = true,
  style,
}: UrlActionButtonsProps) {
  const mounted = useMountedRef();
  const initialCopyTooltip = i18n('workflowTemplate.component.url.actionButtons.copyToClipboard');
  const copiedTooltip = i18n('workflowTemplate.component.url.actionButtons.copiedToClipboard');
  const [copyTooltip, setCopyTooltip] = useState(initialCopyTooltip);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyTooltip(copiedTooltip);
      setTimeout(() => {
        if (mounted.current) {
          setCopyTooltip(initialCopyTooltip);
        }
      }, COPY_TOOLTIP_DURATION_MS);
    } catch (err) {
      // Fallback for browsers that don't support navigator.clipboard
      console.error('Failed to copy to clipboard', err);
    }
  };

  const openUrl = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const shouldRender = showCopy || showOpen;
  if (!shouldRender) {
    return null;
  }

  return (
    <MBView row centerVertically style={style}>
      {showCopy && (
        <RoundIconButton
          icon="squares-stacked"
          onPress={copyToClipboard}
          iconSize={grid(2.75)}
          tooltip={copyTooltip}
        />
      )}
      {showOpen && (
        <RoundIconButton
          icon="arrow-link-out"
          onPress={openUrl}
          style={{ marginLeft: grid(0.5) }}
          tooltip={i18n('workflowTemplate.component.url.actionButtons.openUrl', { url })}
        />
      )}
    </MBView>
  );
}
